<template>
  <div class="preguntas-frecuentes">
    <Titulo titulo="Preguntas frecuentes" />

    <b-row>
      <b-col cols="12" md="8">
        <div class="margin-lateral faq">
          <h4 class="color-secundary mb-2">Resuelva sus dudas sobre el portal transaccional</h4>
          <p class="faq-intro">
            Consulte las respuestas a las inquietudes más comunes sobre los procesos con FGA Fondo de Garantías.
          </p>

          <div v-if="cargando" class="text-center my-5">
            <b-spinner variant="primary" label="Cargando"></b-spinner>
          </div>
          <p v-else-if="error" class="faq-vacio text-danger">{{ error }}</p>
          <p v-else-if="!preguntas.length" class="faq-vacio">Por ahora no hay preguntas frecuentes publicadas.</p>

          <template v-else>
            <!-- Búsqueda -->
            <div class="faq-buscar">
              <label for="faq-buscar" class="sr-only">Buscar una pregunta</label>
              <b-form-input
                id="faq-buscar"
                v-model="busqueda"
                type="search"
                placeholder="Buscar por palabra clave, por ejemplo: novación, saldos, subsanar…"
                autocomplete="off"
              ></b-form-input>
            </div>

            <!-- Filtro por proceso -->
            <div class="faq-categorias" role="tablist" aria-label="Filtrar por proceso">
              <button
                type="button"
                :class="['faq-chip', { activo: categoria === null }]"
                @click="categoria = null"
              >
                Todas <span class="faq-chip-num">{{ preguntas.length }}</span>
              </button>
              <button
                v-for="cat in categorias"
                :key="cat.nombre"
                type="button"
                :class="['faq-chip', { activo: categoria === cat.nombre }]"
                @click="categoria = cat.nombre"
              >
                {{ cat.nombre }} <span class="faq-chip-num">{{ cat.total }}</span>
              </button>
            </div>

            <p v-if="!grupos.length" class="faq-vacio">
              No encontramos preguntas con “{{ busqueda }}”. Intente con otra palabra.
            </p>

            <!-- Preguntas agrupadas por proceso -->
            <section v-for="grupo in grupos" :key="grupo.nombre" class="faq-grupo">
              <h5 class="faq-grupo-titulo">
                {{ grupo.nombre }}
                <small>{{ grupo.items.length }} {{ grupo.items.length === 1 ? "pregunta" : "preguntas" }}</small>
              </h5>

              <div class="faq-item" v-for="item in grupo.items" :key="item.id" :class="{ abierto: abierta === item.id }">
                <button
                  type="button"
                  class="faq-pregunta"
                  :aria-expanded="abierta === item.id ? 'true' : 'false'"
                  :aria-controls="'faq-r-' + item.id"
                  @click="abierta = abierta === item.id ? null : item.id"
                >
                  <span>{{ item.pregunta }}</span>
                  <span class="faq-icono" aria-hidden="true"></span>
                </button>
                <b-collapse :id="'faq-r-' + item.id" :visible="abierta === item.id">
                  <div class="faq-respuesta" v-html="item.respuesta"></div>
                </b-collapse>
              </div>
            </section>
          </template>
        </div>
      </b-col>

      <!-- Panel Derecho -->
      <BannerRight />
    </b-row>
  </div>
</template>

<script>
const PERMISO = "preguntas_frecuentes_portal";

const normalizar = (texto) =>
  (texto || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/<[^>]*>/g, " ");

export default {
  name: "PreguntasFrecuentes",
  components: {
    Titulo: () => import("@/containers/layout/Titulo.vue"),
    BannerRight: () => import("@/containers/layout/banner-right.vue"),
  },
  data() {
    return {
      preguntas: [],
      cargando: true,
      error: "",
      busqueda: "",
      categoria: null,
      abierta: null,
    };
  },
  computed: {
    categorias() {
      const mapa = [];
      this.preguntas.forEach((p) => {
        const nombre = p.categoria || "Otras preguntas";
        const cat = mapa.find((c) => c.nombre === nombre);
        cat ? cat.total++ : mapa.push({ nombre, total: 1 });
      });
      return mapa;
    },
    grupos() {
      const termino = normalizar(this.busqueda).trim();
      return this.categorias
        .filter((c) => this.categoria === null || c.nombre === this.categoria)
        .map((c) => ({
          nombre: c.nombre,
          items: this.preguntas.filter(
            (p) =>
              (p.categoria || "Otras preguntas") === c.nombre &&
              (!termino || normalizar(p.pregunta + " " + p.respuesta).includes(termino))
          ),
        }))
        .filter((g) => g.items.length);
    },
  },
  mounted() {
    const permisos = this.$store.state.user.permisos || [];
    if (!permisos.some((p) => p.descripcion === PERMISO)) {
      this.$router.replace("/home");
      return;
    }
    this.$store.state.api
      .get("/portal/preguntas_frecuentes")
      .then((response) => {
        this.preguntas = response.data.datos || [];
      })
      .catch(() => {
        this.error = "No fue posible cargar las preguntas frecuentes. Intente nuevamente más tarde.";
      })
      .finally(() => {
        this.cargando = false;
      });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.faq {
  padding-bottom: 3rem;
}

.faq-intro {
  color: #5a6b75;
  margin-bottom: 1.5rem;
}

.faq-buscar {
  margin-bottom: 1rem;

  input {
    border-radius: 24px;
    padding: 0.6rem 1.1rem;
    border: 1px solid #c9d6de;

    &:focus {
      border-color: $color-secundary;
      box-shadow: 0 0 0 0.2rem rgba(46, 163, 242, 0.2);
    }
  }
}

.faq-categorias {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.faq-chip {
  border: 1px solid $color-primary;
  background: #fff;
  color: $color-primary;
  border-radius: 20px;
  padding: 0.35rem 0.9rem;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover,
  &:focus {
    background: #e8f4fd;
    outline: none;
  }

  &.activo {
    background: $color-primary;
    color: #fff;

    .faq-chip-num {
      background: rgba(255, 255, 255, 0.25);
      color: #fff;
    }
  }
}

.faq-chip-num {
  display: inline-block;
  min-width: 1.5em;
  margin-left: 0.25rem;
  padding: 0 0.4em;
  border-radius: 10px;
  background: #e8f4fd;
  color: $color-primary;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.faq-grupo {
  margin-bottom: 2rem;
}

.faq-grupo-titulo {
  color: $color-primary;
  font-weight: bold;
  padding-bottom: 0.5rem;
  margin-bottom: 0.75rem;
  border-bottom: 2px solid $color-secundary;

  small {
    color: #7a8a93;
    font-weight: normal;
    font-size: 13px;
    margin-left: 0.5rem;
  }
}

.faq-item {
  border: 1px solid #dfe7ec;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  background: #fff;
  transition: border-color 0.15s;

  &.abierto {
    border-color: $color-secundary;
  }
}

.faq-pregunta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;
  background: none;
  border: 0;
  padding: 0.9rem 1rem;
  color: $color-primary;
  font-weight: bold;
  font-size: 15px;
  cursor: pointer;

  &:focus {
    outline: 2px solid rgba(46, 163, 242, 0.5);
    outline-offset: -2px;
  }
}

/* Signo + / – */
.faq-icono {
  position: relative;
  flex: 0 0 22px;
  height: 22px;
  border-radius: 50%;
  background: #e8f4fd;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 2px;
    background: $color-secundary;
    transform: translate(-50%, -50%);
    transition: transform 0.2s;
  }

  &::after {
    transform: translate(-50%, -50%) rotate(90deg);
  }
}

.abierto .faq-icono::after {
  transform: translate(-50%, -50%) rotate(0deg);
}

.faq-respuesta {
  padding: 0 1rem 1rem;
  color: #3d4b53;
  line-height: 1.6;

  ::v-deep p {
    margin-bottom: 0.6rem;
  }

  ::v-deep ul {
    margin: 0 0 0.6rem;
    padding-left: 1.3rem;
  }

  ::v-deep li {
    margin-bottom: 0.25rem;
  }

  ::v-deep .nota {
    background: #fff8e6;
    border-left: 3px solid #f0b429;
    padding: 0.5rem 0.75rem;
    border-radius: 0 4px 4px 0;
  }
}

.faq-vacio {
  color: #5a6b75;
  padding: 1rem 0;
}

@media (prefers-reduced-motion: reduce) {
  .faq-icono::before,
  .faq-icono::after,
  .faq-chip,
  .faq-item {
    transition: none;
  }
}
</style>
